import React, { useContext } from "react";
import { durationToHuman, getEntryUrl, stripTags, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { Truncate, Thumbnail } from "@kaltura/mediaspace-shared-ui";
import { Config, ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import { Tooltip, Typography } from "@kaltura/ds-react-components";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface ListItemProps {
    name: string;
    description: string;
    image?: string;
    duration?: number;
    entryId: string;
    categoryId?: number;
    customData?: Record<string, string[]>;
    showSpeakers?: boolean | unknown;

    /**
     * in tabs view, media names should be h2, but in vertical view they should be h3 because the section title is h2
     */
    headingLevel?: "h2" | "h3";
}

const StyledListItem = styled("div")(({ theme }) => ({
    display: "flex",
    height: 140,
    borderRadius: 4,
    backgroundColor: theme.kaltura.palette.tone7,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        flexDirection: "column",
        height: "initial",
    },
}));

const StyledImage = styled("div")(({ theme }) => ({
    width: 249,
    "& a": {
        height: "100%",
    },
    "& img": {
        width: "100%",
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        width: "100%",
        height: 193,
        "& img": {
            borderTopRightRadius: 4,
            borderBottomLeftRadius: 0,
        },
    },
}));

const StyledContent = styled("div")(({ theme }) => ({
    padding: theme.spacing(2),
    "& a": {
        color: theme.kaltura.palette.tone1,
        textDecoration: "none",
    },
}));

const StyledDescription = styled("div")(({ theme }) => ({
    color: theme.kaltura.palette.tone2,
    fontWeight: theme.kaltura.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(14),
}));

/**
 * Category page media list item
 */
const ListItem = ({
    name,
    description,
    image = "",
    duration,
    entryId,
    categoryId,
    customData = {},
    showSpeakers = false,
    headingLevel = "h2",
}: ListItemProps) => {
    const config: Config = useContext(ConfigContext);
    const enableEntryTitles = config?.application?.enableEntryTitles;
    const entryName = enableEntryTitles ? name : undefined;
    const linkUrl = getEntryUrl(entryId, categoryId, entryName);

    const theme = useTheme();
    const isTinyScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));

    const getFormattedSpeakers = (speakers: Array<string> | string): string => {
        if (typeof speakers === "string") {
            return translate("Speaker:") + " " + speakers;
        }
        else {
            return translate("Speakers:") + " " + speakers.join("; ");
        }
    };

    // use memo to render description to prevent re-rendering on viewport change, like tabbing
    const truncateDescription = React.useMemo(() => {
        return (
            <Truncate
                lines={isTinyScreen ? 4 : 3}
                showMore={false}
                className={"kms-ds-channel-page-section-media-list-item-description-inner"}
            >
                <>
                    {showSpeakers && customData && customData["Speakers"]?.length > 0 && (
                        <div className="kms-ds-channel-page-section-media-list-speakers">
                            {getFormattedSpeakers(customData["Speakers"])}
                        </div>
                    )}
                    {description && HtmlReactParser(stripTags(description))}
                </>
            </Truncate>
        );
    }, [customData, description, isTinyScreen, showSpeakers]);

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = () => {
        sendButtonAnalytics("Media - Media Thumbnail", ButtonClickAnalyticsType.THUMBNAIL);
    };

    const linkAriaLabelProp = name
        ? duration !== undefined && !Number.isNaN(duration) && duration > 0
            ? translate("Go to %1 with duration: %2", [name, durationToHuman(duration)])
            : translate("Go to %1", [name])
        : "";

    return (
        <StyledListItem className={"kms-ds-channel-page-section-media-list-item-inner"}>
            <StyledImage className={"kms-ds-channel-page-section-media-list-item-thumb-wrap"}>
                <Thumbnail
                    className={!image ? " kms-ds-channel-page-section-media-list-thumb-no-image" : ""}
                    thumbnailUrl={image}
                    duration={duration}
                    linkUrl={linkUrl}
                    ariaLabelText={linkAriaLabelProp}
                    onClick={sendAnalytics}
                />
            </StyledImage>
            <StyledContent className={"kms-ds-channel-page-section-media-list-item-texts"}>
                <Tooltip title={name} placement="top">
                    <a href={linkUrl} aria-label={name} className={"kms-ds-channel-page-section-media-list-item-name"}>
                        <Typography variant="h4" component={headingLevel}>
                            {name}
                        </Typography>
                    </a>
                </Tooltip>
                <StyledDescription className={"kms-ds-channel-page-section-media-list-item-description"}>
                    {truncateDescription}
                </StyledDescription>
            </StyledContent>
        </StyledListItem>
    );
};

export default ListItem;
